import React, { useRef, useState, useEffect, Fragment } from 'react';

import * as Sentry from '@sentry/react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { base64ToFile } from 'utils/generic';

import ExpandedImage from './components/ExpandedImage';
import { Container, UploadButton, Label, Image, Trash } from './styles';

interface Props {
  currentFile?: File;
  deleteFileText?: string;
  onChange: (file?: File, type?: string) => void;
}

const ImagePicker = ({ currentFile, deleteFileText, onChange }: Props) => {
  const { t } = useStoreTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File>();
  const [src, setSrc] = useState<string>();
  const [isZoom, setZoom] = useState<boolean>(false);

  useEffect(() => {
    setFile(currentFile);
  }, [currentFile]);

  useEffect(() => {
    if (file) {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file as File);
        reader.onload = () => {
          setSrc(reader.result as string);
        };
      } catch (e) {
        Sentry.setExtra('error', e);
        Sentry.captureMessage('[Expenses] Could not load image');
      }
    }
  }, [file]);

  useEffect(() => {
    window.webUploader = {
      retrieveImageFromDevice: webViewRetrieveImageFromDevice,
    };
    // eslint-disable-next-line
  }, []);

  //metodo injetado por la webview para iOS y Android
  function webViewRetrieveImageFromDevice(imageBase64) {
    try {
      const mimeString = imageBase64.split(',')[0].split(':')[1].split(';')[0].split('/')[1];
      const name = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

      base64ToFile(imageBase64, mimeString, name).then((file: File) => {
        setFile(file);
        onChange(file, file?.type);
      });
    } catch (e) {
      Sentry.setExtra('image', imageBase64);
      Sentry.setExtra('error', e);
      Sentry.captureMessage('[Expenses] Could not read image from webview');
    }
  }

  const handleUpload = () => {
    if (window.app !== undefined) {
      //si es Android
      window.app?.requestCameraPermissions();
    } else {
      //si es desktop o iOS
      inputRef.current?.click();
    }
  };

  const handleDiscard = () => {
    setZoom(false);
    setFile(undefined);
    onChange(undefined);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleZoom = () => {
    setZoom(true);
  };

  const handleCloseZoom = () => {
    setZoom(false);
  };

  const handleChangePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = event.target.files?.item(0) || undefined;
    setFile(newFile);
    onChange(newFile, newFile?.type);
  };

  return (
    <>
      <Container>
        {file && (
          <Fragment>
            <Image onClick={handleZoom}>
              <Image.Src id="image_output" src={src} alt="image" />
              <Image.Zoom src="/images/zoom-outline-12.svg" alt="zoom" />
            </Image>
            {deleteFileText && (
              <Trash onClick={handleDiscard}>
                <label>{deleteFileText}</label>
                <img src="/images/trash-can.svg" alt="trash" />
              </Trash>
            )}
          </Fragment>
        )}
        {!file && (
          <Fragment>
            <Label>{t('imagePicker.label')}</Label>
            <UploadButton type="button" invertedColor onClick={handleUpload}>
              <span>{t('imagePicker.buttonAdd')}</span>
              <input type="file" accept="image/*" ref={inputRef} onChange={handleChangePhoto} />
            </UploadButton>
          </Fragment>
        )}
      </Container>
      <ExpandedImage show={isZoom} src={src} onClose={handleCloseZoom} />
    </>
  );
};

export default ImagePicker;
